import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Lock } from "lucide-react";

const STORAGE_KEY = "styleguide-auth";

/**
 * Client-side password gate for /styleguide. The password is read from
 * VITE_STYLEGUIDE_PASSWORD and baked into the bundle — this is a soft gate
 * (anyone determined enough to dig into the JS can find it), adequate for
 * keeping the page out of Google and off random visitors. Do not put sensitive
 * data behind this.
 */
export function StyleguideGate({ children }: { children: ReactNode }) {
  const expected = (import.meta.env.VITE_STYLEGUIDE_PASSWORD ?? "").trim();
  const [authed, setAuthed] = useState(false);
  const [entered, setEntered] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!expected) {
      setAuthed(true);
      return;
    }
    if (sessionStorage.getItem(STORAGE_KEY) === expected) {
      setAuthed(true);
    }
  }, [expected]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (entered === expected) {
      sessionStorage.setItem(STORAGE_KEY, expected);
      setAuthed(true);
      setError(null);
    } else {
      setError("Feil passord.");
    }
  };

  if (authed) return <>{children}</>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-lg">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Lock size={20} aria-hidden="true" />
        </div>
        <h1 className="mt-5 text-center font-heading text-xl font-bold">
          Styleguide
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground font-body">
          Intern referanseside. Angi passord for å fortsette.
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-3">
          <label htmlFor="styleguide-password" className="sr-only">
            Passord
          </label>
          <input
            id="styleguide-password"
            type="password"
            value={entered}
            onChange={(event) => setEntered(event.target.value)}
            autoFocus
            className="w-full rounded-full border border-input bg-background px-5 py-3 text-sm font-body outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Passord"
          />
          {error && (
            <p className="text-center text-sm text-destructive font-body" role="alert">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="w-full rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground font-body shadow-md transition-all hover:opacity-90"
          >
            Logg inn
          </button>
        </form>
      </div>
    </div>
  );
}
